import { HTMLProps, RefAttributes, useId } from "react";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import { cn } from "@/utils/helpers";
import TooltipOverlay from "./components/TooltipOverlay";
import { SvgIconInfo, SvgIconProps } from "@/components/common/Svg";
import styles from "./Tooltip.module.css";

export interface TooltipProps extends HTMLProps<HTMLDivElement> {
	inner?: string;
	footer?: string;
	persist?: boolean;
	side?: "bottom" | "top" | "right" | "left";
	align?: "start" | "center" | "end";
	iconColor?: SvgIconProps["color"];
	hasIcon?: boolean;
	TriggerProps?: RadixTooltip.TooltipTriggerProps;
	ContentProps?: RadixTooltip.TooltipContentProps & RefAttributes<HTMLDivElement>;
};

export default function Tooltip({
	inner,
	footer,
	persist,
	side = "bottom",
	align = "center",
	hasIcon,
	iconColor,
	children,
	className,
	TriggerProps,
	ContentProps,
	...props
} : TooltipProps) {
	const id = useId();
	return (
		inner ?
			<RadixTooltip.Provider>
				<RadixTooltip.Root
					delayDuration={100}
					{...props}
				>
					<RadixTooltip.Trigger
						asChild={true}
						className={cn(
							className,
							styles.TooltipTrigger,
							persist ? styles.TooltipTrigger_persist : null,
							hasIcon ? styles.TooltipTrigger_hasIcon : null,
						)}
						{...TriggerProps}
					>
						<div
							role={hasIcon ? "button" : ""}
						>
							{hasIcon ?
								<>
									{children ?
										<span>{children}</span>
									: null}
									<SvgIconInfo
										color={iconColor}
										className={styles.TooltipTriggerIcon}
									/>
								</>
							: children}
						</div>
					</RadixTooltip.Trigger>
					<RadixTooltip.Portal>
						<RadixTooltip.Content
							side={side}
							align={align}
							sideOffset={5}
							collisionPadding={6}
							{...ContentProps}
							className={cn(
								styles.TooltipContent,
								ContentProps?.className
							)}
						>
							<RadixTooltip.Arrow
								className={styles.TooltipArrow}
							/>
							<TooltipOverlay
								id={id}
								footer={footer}
								persist={persist}
							>
								{inner}
							</TooltipOverlay>
						</RadixTooltip.Content>
					</RadixTooltip.Portal>
				</RadixTooltip.Root>
			</RadixTooltip.Provider>
		: children
	);
};